<template>
  <div class="min-h-screen bg-gradient-to-br from-[#10103c] via-[#1a1a5c] to-[#2a2a7c] text-white">
    <!-- Main Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-32">
      <div class="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8 md:p-12">
        <h1 class="text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Terms of Use
        </h1>
        
        <div class="prose prose-invert max-w-none">
          <!-- Governance Disclosure -->
          <div class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-6 mb-8">
            <h2 class="text-xl font-semibold text-yellow-300 mb-3">🏛️ Governance Notice</h2>
            <p class="text-yellow-100">
              <strong>Important:</strong> Rendery Consortium operates as a cooperative organization. These Terms of Use can be modified through our democratic governance process, where members of the cooperative can vote for or against proposed changes. By using this platform, you acknowledge and agree to be bound by any future modifications approved through our governance mechanism.
            </p>
          </div>

          <!-- Introduction -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">1. Introduction</h2>
            <p class="mb-4">
              Welcome to Rendery Consortium, a cooperative platform that enables video content creators to license their content directly to artificial intelligence companies. By accessing or using our platform, you agree to be bound by these Terms of Use.
            </p>
            <p>
              Rendery Consortium operates as a cooperative organization, where members collectively govern the platform and make decisions about its operation and policies.
            </p>
          </section>

          <!-- Platform Purpose -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">2. Platform Purpose</h2>
            <p class="mb-4">
              Rendery Consortium facilitates the licensing of video content for AI training purposes. Our platform connects content creators with AI companies seeking high-quality training data while ensuring creators maintain control over their intellectual property and receive fair compensation.
            </p>
          </section>

          <!-- Joint Offering -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">3. Joint Offering Structure</h2>
            <p class="mb-4">
              <strong>Cooperative Licensing Model:</strong> By joining Rendery Consortium, members agree to participate in a joint offering structure for licensing their video content. This means:
            </p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
                              <li>Companies licensing video content will engage with Rendery Consortium as a single, combined entity representing all participating creators</li>
                <li>Members agree to allow Rendery Consortium to calculate and distribute respective payouts based on established methodologies</li>
              <li>The joint offering structure enables better negotiating power and streamlined licensing processes for AI companies</li>
            </ul>
            <p class="mb-4">
              <strong>Payout Methodology:</strong> Rendery Consortium will calculate member payouts based on factors including but not limited to:
            </p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Content quality and uniqueness</li>
              <li>Volume of licensed content</li>
              <li>Demand and usage metrics</li>
              <li>Cooperative participation and governance involvement</li>
            </ul>
            <p class="mb-4">
              <strong>Transparency Commitment:</strong> The complete methodology used to calculate payouts shall be made available to any member upon request. Changes to the payout methodology can only be implemented through the democratic governance process outlined in these terms.
            </p>
          </section>

          <!-- Content Licensing and Productization -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">4. Content Licensing and Productization Rights</h2>
            <p class="mb-4">
              By participating in Rendery Consortium, you grant representatives of Rendery Consortium the right to productize your videos for licensing purposes, which includes but is not limited to:
            </p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Creation of clips and excerpts from your original content</li>
              <li>Automatic generation of transcripts and captions</li>
              <li>Metadata extraction and content categorization</li>
              <li>Format conversion and optimization for AI training datasets</li>
              <li>Quality assessment and content verification processes</li>
            </ul>
            <p>
              These productization activities are performed solely to facilitate the licensing process and enhance the value of your content for AI training purposes. You retain ownership of your original content and intellectual property rights.
            </p>
          </section>

          <!-- Prohibited AI Use Cases -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">5. Prohibited AI Use Cases</h2>
            <p class="mb-4">
              Rendery Consortium explicitly prohibits the following use cases for AI training with licensed content:
            </p>
            
            <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-6 mb-6">
              <h3 class="text-lg font-semibold text-red-300 mb-3">🚫 Strictly Forbidden Uses:</h3>
              <ul class="list-disc pl-6 space-y-2">
                <li><strong>Content Replication:</strong> Direct replication or recreation of video content, including but not limited to changing narrators, voices, or presenters while maintaining the same content structure and information</li>
                <li><strong>Deepfakes and Impersonation:</strong> Creation of deepfake content, voice cloning, or any form of digital impersonation of content creators</li>
                <li><strong>Nefarious Applications:</strong> Use for harassment, defamation, misinformation, fraud, or any illegal activities</li>
                <li><strong>Unauthorized Reproduction:</strong> Creating derivative works that substantially reproduce the original content without explicit permission</li>
                <li><strong>Identity Theft:</strong> Using creator likenesses, voices, or personas for unauthorized commercial purposes</li>
                <li><strong>Harmful Content Generation:</strong> Training models to generate content that promotes violence, hate speech, or discrimination</li>
              </ul>
            </div>

            <p>
              AI companies licensing content through Rendery Consortium must agree to these restrictions and implement appropriate safeguards to prevent prohibited uses.
            </p>
          </section>

          <!-- Creator Authenticity and Acknowledgment -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">6. Creator Authenticity and Acknowledgment</h2>
            <p class="mb-4">
              By participating in Rendery Consortium, you acknowledge and warrant that:
            </p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>You are the original creator and owner of all content you submit to the platform</li>
              <li>You are not impersonating another content creator or individual</li>
              <li>All information provided during the onboarding questionnaire and registration process is accurate and truthful</li>
              <li>You have the legal right and authority to license the content you submit</li>
              <li>Your content does not infringe upon the intellectual property rights of any third party</li>
                              <li>You will promptly notify Rendery Consortium of any changes to your ownership status or legal rights regarding your content</li>
            </ul>
            <p>
              <strong>Verification:</strong> Rendery Consortium reserves the right to verify creator authenticity and may request additional documentation to confirm your identity and ownership of submitted content.
            </p>
          </section>

          <!-- Governance and Terms Modification -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-purple-300">7. Governance and Terms Modification</h2>
            <p class="mb-4">
              <strong>Democratic Process:</strong> As a cooperative organization, Rendery operates under democratic governance principles:
            </p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Terms of Use modifications require a formal proposal and voting process</li>
              <li>All cooperative members have voting rights on proposed changes</li>
              <li>Proposals must meet quorum requirements and pass by majority vote</li>
              <li>Members will be notified of proposed changes with adequate time for review</li>
              <li>Approved changes take effect after a specified implementation period</li>
            </ul>
            <p>
              <strong>Notification:</strong> Users will be notified of any approved changes via email and platform announcements. Continued use of the platform after changes take effect constitutes acceptance of the modified terms.
            </p>
          </section>

          <!-- User Obligations -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">8. User Obligations</h2>
            <p class="mb-4">By using Rendery Consortium, you agree to:</p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Provide accurate information about your content and ownership rights</li>
              <li>Only submit content you own or have proper licensing rights to</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Respect the intellectual property rights of others</li>
              <li>Participate constructively in the cooperative governance process</li>
              <li>Report any suspected violations of these terms</li>
            </ul>
          </section>

          <!-- Intellectual Property -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">9. Intellectual Property</h2>
            <p class="mb-4">
              <strong>Creator Rights:</strong> You retain all ownership rights to your original content. Rendery Consortium facilitates licensing but does not claim ownership of your intellectual property.
            </p>
            <p class="mb-4">
              <strong>Platform Rights:</strong> Rendery Consortium retains rights to its platform technology, branding, and proprietary systems.
            </p>
            <p>
              <strong>Licensed Use:</strong> AI companies receive only the specific usage rights outlined in their licensing agreements, not ownership of the content.
            </p>
          </section>

          <!-- Termination -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">10. Termination</h2>
            <p class="mb-4">
              Either party may terminate their participation in Rendery Consortium at any time. Upon termination:
            </p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Existing licensing agreements remain in effect</li>
              <li>Member data will be removed within 90 days</li>
              <li>Cooperative membership rights and obligations cease</li>
              <li>Outstanding payments will be processed according to platform policies</li>
            </ul>
          </section>

          <!-- Limitation of Liability -->
          <section class="mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-blue-300">11. Limitation of Liability</h2>
            <p class="mb-4">
              <strong>Platform Protection:</strong> Rendery Consortium members, administrators, developers, and all associated parties are protected from liability related to:
            </p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Use or misuse of the platform by third parties</li>
              <li>Actions taken by AI companies using licensed content</li>
              <li>Technical issues, downtime, or data loss</li>
              <li>Disputes between creators and AI companies</li>
              <li>Supporting infrastructure failures or security breaches</li>
              <li>Economic losses resulting from platform participation</li>
            </ul>
            <p>
              <strong>User Responsibility:</strong> Users acknowledge that they participate in Rendery Consortium at their own risk and agree to hold harmless all platform stakeholders from any claims, damages, or losses arising from their use of the platform.
            </p>
          </section>

          <!-- Effective Date -->
          <section class="mb-8">
            <div class="bg-gray-500/20 border border-gray-500/30 rounded-lg p-4 text-center">
              <p class="text-sm text-gray-300">
                <strong>Effective Date:</strong> These Terms of Use are effective as of May 15, 2025
              </p>
              <p class="text-sm text-gray-300 mt-2">
                <strong>Last Updated:</strong> May 15, 2025
              </p>
            </div>
          </section>
        </div>

        <!-- Back to Home Button -->
        <div class="text-center mt-12">
          <router-link 
            to="/" 
            class="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
          >
            ← Back to Home
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TermsOfUse',
  mounted() {
    // Set page title
    document.title = 'Terms of Use - Rendery Consortium';
  }
};
</script>

<style scoped>
/* Custom prose styling for better readability */
.prose h2 {
  border-bottom: 2px solid rgba(59, 130, 246, 0.3);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.prose h3 {
  margin-bottom: 0.75rem;
}

.prose p {
  margin-bottom: 1rem;
  line-height: 1.7;
}

.prose ul {
  margin-bottom: 1rem;
}

.prose li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

/* Smooth scrolling for anchor links */
html {
  scroll-behavior: smooth;
}
</style> 